/**
 * Local session identity, the only thing that makes a reload resumable.
 *
 * `sessionStorage`, deliberately: it survives F5 in the same tab and dies with
 * the tab. A session rediscovered in a new context therefore has no local
 * identity and is NOT resumable — the UI offers Discard/Start over rather than
 * accepting a newly selected file on size alone.
 */
export interface StoredMultipartSession {
	fileId: string
	multipartSessionId: string
	uploadAttemptId: string
	/** Identity of the source file, matched exactly before a single byte is resumed. */
	originalName: string
	/** A client-side identity hint copied from the backend response; the backend never trusts it as a size. */
	declaredFileSize: number
	lastModified: number
	partSize: number
	partCount: number
}

const STORAGE_KEY = 'atlas.upload.multipart.sessions'

/** The identity a reload must reproduce exactly: name + size + lastModified. Nothing weaker resumes. */
export function fileIdentityKey(file: { name: string; size: number; lastModified: number }): string {
	return `${file.name}::${file.size}::${file.lastModified}`
}

function readAll(): Record<string, StoredMultipartSession> {
	try {
		const raw = globalThis.sessionStorage?.getItem(STORAGE_KEY)
		return raw ? (JSON.parse(raw) as Record<string, StoredMultipartSession>) : {}
	} catch {
		// A private-mode/quota-blocked storage is not an upload failure: it only
		// means this upload cannot be resumed after a reload.
		return {}
	}
}

function writeAll(entries: Record<string, StoredMultipartSession>): void {
	try {
		globalThis.sessionStorage?.setItem(STORAGE_KEY, JSON.stringify(entries))
	} catch {
		// Same reasoning as readAll: resumability is best-effort, the transfer is not.
	}
}

export function rememberSession(
	file: { name: string; size: number; lastModified: number },
	session: StoredMultipartSession,
): void {
	const entries = readAll()
	entries[fileIdentityKey(file)] = session
	writeAll(entries)
}

/**
 * The stored session for this exact file, if any. A file whose name, size or
 * `lastModified` differs is a different file, and is never offered the stored
 * session — that is the whole resume-identity rule.
 */
export function findSession(file: { name: string; size: number; lastModified: number }): StoredMultipartSession | null {
	return readAll()[fileIdentityKey(file)] ?? null
}

export function forgetSession(file: { name: string; size: number; lastModified: number }): void {
	const entries = readAll()
	delete entries[fileIdentityKey(file)]
	writeAll(entries)
}
